import React from 'react'
import { SubmitHandler, useForm, Controller } from 'react-hook-form'
import { Task } from '@core/entities/task/Task'
import { useModal } from '@common/GlobalModal'
import { useToast } from '@core/toast'
import { useCancelTask } from '@core/react-query/features/tasks/task'
import { localizedStrings } from '@core/strings'
import {
  DialogTitle,
  IconButton,
  DialogContent,
  Button,
  TextField,
} from '@common/material'
import { Flex } from '@fundamentals'
import { ModalDialogContainer } from '@common/modal'
import ClearIcon from '@mui/icons-material/Clear'
import { MAX_TASK_SUMMARY_CHARACTERS } from '@core/components/templateAnswersForm'

interface ComponentType {
  task: Task
}

type FormData = {
  notes: string
}

export const CancelTaskModal: React.FC<ComponentType> = ({ task }) => {
  const toast = useToast()
  const { hideModal } = useModal()
  const { control, handleSubmit } = useForm({
    defaultValues: {
      notes: '',
    },
  })

  const cancelTaskMutation = useCancelTask()

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    cancelTaskMutation.mutate(
      {
        id: task.id,
        notes: data.notes,
      },
      {
        onSuccess: () => {
          toast.success(localizedStrings.taskCancelledSuccessfully)
        },
        onError: (error) => {
          toast.error(error.message)
        },
        onSettled: () => {
          hideModal()
        },
      },
    )
  }

  return (
    <ModalDialogContainer>
      <DialogTitle
        variant='h5'
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        sx={{ pr: 1, fontSize: 20 }}
      >
        {localizedStrings.cancelTask}
        <IconButton aria-label='close-modal' onClick={hideModal}>
          <ClearIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Flex flexDirection={'column'} sx={{ mt: 2 }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Controller
              name='notes'
              control={control}
              render={({ field: { onChange, ...field }, fieldState }) => {
                return (
                  <TextField
                    label={localizedStrings.note}
                    placeholder={localizedStrings.note}
                    data-test='note-input'
                    rows={3}
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error?.message ||
                      `${
                        field.value?.length || 0
                      }/${MAX_TASK_SUMMARY_CHARACTERS} characters`
                    }
                    onChange={(e) =>
                      e.target.value.length <= MAX_TASK_SUMMARY_CHARACTERS &&
                      onChange(e)
                    }
                    slotProps={{
                      formHelperText: {
                        error: !!fieldState.error,
                        sx: { pt: 0.5, pb: 2 },
                      },
                    }}
                    fullWidth
                    multiline
                    {...field}
                  />
                )
              }}
            />

            <Flex gap={2} mt={4}>
              <Button variant='outlined' onClick={hideModal} fullWidth>
                {localizedStrings.cancel}
              </Button>
              <Button
                variant='contained'
                type='submit'
                fullWidth
                data-test='save-task'
                disabled={cancelTaskMutation.isLoading}
              >
                {localizedStrings.save}
              </Button>
            </Flex>
          </form>
        </Flex>
      </DialogContent>
    </ModalDialogContainer>
  )
}
